import { CheckRounded } from "@mui/icons-material";
import {
  Box,
  Button,
  Drawer,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Product } from "component/MarketPlace/ProductCard/types";
import DefaultUptimeTheme from "component/theme/defaultTheme";
import React, { useEffect, useState } from "react";
import { useTheme } from "styled-components";
import { StyledTitle } from "../ProductDetail.styles";
import { StyledGrid } from "./ProductCompare.style";
import ProductCompareCard from "./ProductCompareCard";
import ProductComapreTable from "./ProductCompareTable";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import InfoIcon from "component/commonComponents/Icon/InfoIcon";

const ProductCompare = ({
  currentProduct,
  compareProductList,
  title,
  handleAddToCart,
  hideUnavailable,
  showAll,
  locale,
}: {
  currentProduct: Product;
  compareProductList: Product[];
  title: string;
  handleAddToCart: (product: Product) => void;
  hideUnavailable?: boolean;
  showAll?: boolean;
  locale: any;
}) => {
  const compareProducts = compareProductList?.filter(
    (product: Product, index) =>
      compareProductList.findIndex(
        (item: Product) =>
          product.sku === item.sku &&
          JSON.stringify(product.sku) === JSON.stringify(item.sku)
      ) === index
  );

  const [compareList, setCompareList] = React.useState<Product[]>(compareProducts);
  const [selectProduct, setSelectProduct] = React.useState<Product[]>([...Array(3)]);

  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [drawerSelected, setDrawerSelected] = React.useState("");
  const [drawerSelectedProduct, setDrawerSelectedProduct] = useState<Product>(selectProduct[0]);

  const theme = useTheme() as typeof DefaultUptimeTheme;
  const mdUp = useMediaQuery(`(min-width:${theme.breakpoints.md})`);

  useEffect(() => {
    setCompareList(compareProducts);
  }, [compareProductList]);

  const handleSelectorChange = (sku: string, index: number) => {
    if (sku !== "default") {
      const product:Product = compareProducts.find((item) => item.sku === sku) || undefined as unknown as Product;
      let productList = [...selectProduct];
      productList[index] = product!;
      setDrawerSelectedProduct(productList[0]);
      setSelectProduct(productList);

      const selectIdList = productList.map((item:Product) => item?.sku);
      const list = compareProducts.filter(({sku}) => !selectIdList.includes(sku));
      setCompareList(list);
    } else {
      const list = [...compareList, selectProduct[index]];
      setCompareList(list);

      let removedItemList = [...selectProduct];
      removedItemList[index] = undefined as unknown as Product;
      setDrawerSelectedProduct(removedItemList[0]);
      setSelectProduct(removedItemList);
    }
  };

  const handleDrawerSelect = (sku: string) => {
    setDrawerSelected(sku);
    const product:Product = compareProducts.find((item) => item.sku === sku) || undefined as unknown as Product;
    let productList = [...selectProduct];
    productList[0] = product!;
    setSelectProduct(productList);
    setDrawerSelectedProduct(product);

    const selectIdList = productList.map((item:Product) => item?.sku);
    const list = compareProducts.filter(({sku}) => !selectIdList.includes(sku));
    setCompareList(list);

    setOpenDrawer(false);
  };

  const ProductDrawer = () => {
    return (
      <Drawer open={openDrawer} anchor="bottom" PaperProps={{sx:{maxHeight:"336px"}}}>
        <Box overflow="auto" flex="1" padding={`${theme.uptimeSpace[12]}px`}>
          <Typography variant="h4" marginBottom={`${theme.uptimeSpace[10]}px`}>
            {locale?.productDetail?.chooseAComputer}
          </Typography>
          {compareList.map((product: Product) => (
            <MenuItem
              key={product.sku}
              value={product.sku}
              onClick={() => handleDrawerSelect(product.sku)}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor:
                  drawerSelected === product.sku
                    ? theme.uptimeColors.mainTheme.background
                    : "",
              }}
            >
              <Typography>{product.name}</Typography>
              {drawerSelected === product.sku ? (
                <CheckRounded sx={{ color: "primary.main" }} />
              ) : (
                ""
              )}
            </MenuItem>
          ))}
        </Box>
      </Drawer>
    );
  };

  return (
    <>
      {/* title */}
      <Box mb="32px">
        <StyledTitle variant="h3" color="text.primary">
          {title}
        </StyledTitle>
      </Box>
      {/* card */}
      <Box mb="32px">
        <Grid container spacing={3}>
          {/* current item */}
          <Grid item xs={6} md={4} lg={3}>
            <Box mb="10px">
              <InputLabel
                variant="standard"
                sx={{
                  color: "text.secondary",
                  fontSize: "12px",
                  lineHeight: "12px",
                  weight: "400",
                }}
              >
                {locale?.productDetail?.current}
              </InputLabel>
              <TextField
                variant="standard"
                aria-label="Current Product Input"
                value={currentProduct.name}
                fullWidth
                InputProps={{ readOnly: true, disableUnderline: true}}
                inputProps={{"aria-label": currentProduct.name}}
                sx={{
                  borderBottom: "1px dotted #0000006B",
                }}
              />
            </Box>
            <ProductCompareCard
              product={currentProduct}
              // action={
              //   <Button
              //     variant="contained"
              //     onClick={() => handleAddToCart(currentProduct)}
              //   >
              //     {locale?.addToCart}
              //   </Button>
              // }
              locale={locale}
            />
          </Grid>
          {/* compare items */}
          {mdUp ? (
            selectProduct.map((product: Product, index: number) => (
              <StyledGrid
                item
                xs={6}
                md={4}
                lg={3}
                key={`compare-column-${index}`}
              >
                <Box mb="10px">
                  <InputLabel
                    variant="standard"
                    sx={{
                      color: `${product?.sku ? "text.secondary" : "white"}`,
                      fontSize: "12px",
                      lineHeight: "12px",
                      weight: "400",
                    }}
                  >
                    {locale?.productDetail?.computer}
                  </InputLabel>
                  <Select
                    id={`drp-productcompare-product-${index}`}
                    variant="standard"
                    fullWidth
                    defaultValue="default"
                    value={product?.sku || "default"}
                    disabled={!Array.isArray(compareList) || compareList.length === 0}
                    onChange={(e: any) =>
                      handleSelectorChange(e.target.value, index)
                    }
                    MenuProps={{
                      disableScrollLock: true,
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      MenuListProps: {
                        style: {
                          maxHeight: "240px",
                        },
                      },
                      sx: {
                        zIndex: "1000 !important",
                      }
                    }}
                    inputProps={{"aria-hidden": false, "tabindex": 0, "title": `above-md-selector-${index}`}}
                    sx={{
                      borderBottom: "1px solid #0000006b",
                      '&::before':{
                        content: 'none'
                      },
                      '&::after':{
                        content: 'none'
                      },
                      '&:hover': {
                        borderBottom: "1px solid #000000DE",
                      }
                    }}
                  >
                    <MenuItem key="default" value="default">
                      <Typography sx={{ color: "primary.main" }}>
                        {locale?.productDetail?.chooseAComputer}
                      </Typography>
                    </MenuItem>
                    {!!product?.sku && (
                      <MenuItem key={product?.sku} value={product?.sku}>
                        <Typography
                          sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "pre",
                          }}
                        >
                          {product?.name}
                        </Typography>
                      </MenuItem>
                    )}
                    {compareList.map((option: Product, itemIndex) => (
                      <MenuItem key={option.sku} value={option.sku} id={`drp-productcompare-product-${index}-${itemIndex}`}>
                        <Typography
                          sx={{
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "pre",
                          }}
                        >
                          {option.name}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
                <ProductCompareCard
                  product={product}
                  action={
                    <Button
                      disabled={product?.allowed_to_order === false}
                      variant="contained"
                      onClick={() => handleAddToCart(product)}
                    >
                      {locale?.productDetail?.select}
                      {product?.allowed_to_order === false && (
                        <Tooltip title={locale?.softwareSubscription?.limitWarning}>
                          <InfoIcon/>
                        </Tooltip>
                      )}
                    </Button>
                  }
                  locale={locale}
                />
              </StyledGrid>
            ))
          ) : (
            <Grid item xs={6} md={4} lg={3}>
              <Box mb="10px">
                <InputLabel
                  variant="standard"
                  sx={{
                    color: `${
                      drawerSelectedProduct?.sku ? "text.secondary" : "white"
                    }`,
                    fontSize: "12px",
                    lineHeight: "12px",
                    weight: "400",
                  }}
                >
                  {locale?.productDetail?.computer}
                </InputLabel>
                <TextField
                  variant="standard"
                  value={drawerSelectedProduct?.name || "Choose a Computer"}
                  fullWidth
                  onClick={() => setOpenDrawer(true)}
                  InputProps={{
                    readOnly: true,
                    endAdornment: <ArrowDropDownIcon />,
                    sx: {
                      color: `${
                        !!drawerSelectedProduct?.name
                          ? "text.primary"
                          : "primary.main"
                      }`,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "pre",
                    },
                    "aria-label": "Compare Computer Input"
                  }}
                />
              </Box>
              <ProductCompareCard
                product={drawerSelectedProduct}
                action={
                  <Button
                    variant="contained"
                    onClick={() => handleAddToCart(drawerSelectedProduct)}
                  >
                    {locale?.productDetail?.select}
                  </Button>
                }
                locale={locale}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      {/* table */}
      <ProductComapreTable
        currentProduct={currentProduct}
        compareProductList={selectProduct}
        hideUnavailable={hideUnavailable}
        showAll={showAll}
        locale={locale}
      />
      {!mdUp && <ProductDrawer />}
    </>
  );
};

export default ProductCompare;
